import '../css/app.css';

import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';

Alpine.plugin(collapse);
window.Alpine = Alpine;
Alpine.start();

import '@fontsource/lato/100.css';
import '@fontsource/lato/300.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/lato/900.css';

import { createApp } from 'vue';
import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import axiosPlugin from './plugins/axios.js';
import { de } from "primelocale/js/de.js";
import { en } from "primelocale/js/en.js";

const primeLocales = { de, en };

const componentImports = import.meta.glob('./components/**/*.vue');

function pathToComponentName(path) {
    return path
        .split('/')
        .pop()
        .replace('.vue', '')
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace(/^-/, '');
}

const primeVueConfig = {
    theme: {
        preset: Aura,
        options: {
            prefix: 'p',
            darkModeSelector: false,
            cssLayer: false
        }
    },
    locale: primeLocales[window.__locale] || de,
    pt: {
        global: {
            css: `
                :root {
                    --p-primary-50: color-mix(in srgb, rgb(var(--color-primary)) 8%, white);
                    --p-primary-100: color-mix(in srgb, rgb(var(--color-primary)) 12%, white);
                    --p-primary-200: color-mix(in srgb, rgb(var(--color-primary)) 20%, white);
                    --p-primary-300: color-mix(in srgb, rgb(var(--color-primary)) 35%, white);
                    --p-primary-400: color-mix(in srgb, rgb(var(--color-primary)) 55%, white);
                    --p-primary-500: rgb(var(--color-primary));
                    --p-primary-600: color-mix(in srgb, rgb(var(--color-primary)), black 12%);
                    --p-primary-700: color-mix(in srgb, rgb(var(--color-primary)), black 25%);
                    --p-primary-800: color-mix(in srgb, rgb(var(--color-primary)), black 40%);
                    --p-primary-900: color-mix(in srgb, rgb(var(--color-primary)), black 55%);
                    --p-primary-950: color-mix(in srgb, rgb(var(--color-primary)), black 70%);

                    --p-primary-color: rgb(var(--color-primary));
                    --p-primary-contrast-color: #ffffff;
                    --p-primary-hover-color: color-mix(in srgb, rgb(var(--color-primary)), black 12%);
                    --p-primary-active-color: color-mix(in srgb, rgb(var(--color-primary)), black 25%);

                    --p-highlight-background: color-mix(in srgb, rgb(var(--color-primary)) 8%, white);
                    --p-highlight-focus-background: color-mix(in srgb, rgb(var(--color-primary)) 12%, white);
                    --p-highlight-color: color-mix(in srgb, rgb(var(--color-primary)), black 25%);
                    --p-highlight-focus-color: color-mix(in srgb, rgb(var(--color-primary)), black 40%);

                    --p-focus-ring-color: rgb(var(--color-primary));
                }
            `
        }
    }
};

async function mountComponent(el, componentName, importFn) {
    const module = await importFn();
    const component = module.default;

    const props = {};
    Array.from(el.attributes).forEach(attr => {
        const propName = attr.name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        props[propName] = attr.value;
    });

    const app = createApp(component, props);
    app.use(axiosPlugin);
    app.use(PrimeVue, primeVueConfig);
    app.mount(el);
}

async function autoMountComponents() {
    const mountPromises = [];

    for (const [path, importFn] of Object.entries(componentImports)) {
        const componentName = pathToComponentName(path);
        const elements = document.querySelectorAll(componentName);

        if (elements.length > 0) {
            elements.forEach(el => {
                mountPromises.push(mountComponent(el, componentName, importFn));
            });
        }
    }

    await Promise.all(mountPromises);
}

document.addEventListener('DOMContentLoaded', autoMountComponents);
